import React, {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {VariableSizeList} from 'react-window';

export function useMeasuredPostRow(ref, index, enabled, setRowHeight) {
    useLayoutEffect(() => {
        const element = ref.current;
        if(!enabled || !element) return;
        const measure = () => setRowHeight(index, element.clientHeight);
        measure();
        if(typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref, index, enabled, setRowHeight]);
}

// AutoSizer can change geometry without rerendering InfiniteGrid. Own both
// caches here so visited offscreen rows cannot retain an earlier card width.
export default function MeasuredPostsList({itemData, width, onListRef, children, ...props}) {
    const list = useRef();
    const pendingReset = useRef(null);
    const heights = useMemo(() => ({}), [width, itemData.assetSize, itemData.assetMargin]);
    const setRowHeight = useCallback((index, height) => {
        if(height <= 0 || heights[index] === height) return;
        heights[index] = height;
        if(list.current) list.current.resetAfterIndex(index);
        else pendingReset.current = Math.min(pendingReset.current ?? index, index);
    }, [heights]);
    const itemSize = useCallback(index => heights[index] || 400, [heights]);
    const data = useMemo(() => ({...itemData, setRowHeight}), [itemData, setRowHeight]);
    const attachList = useCallback(node => {
        list.current = node;
        onListRef(node);
    }, [onListRef]);

    useLayoutEffect(() => {
        // Child layout effects may already have measured the new geometry.
        // Invalidate react-window's offsets without erasing those measurements.
        list.current?.resetAfterIndex(0);
    }, [heights]);
    useLayoutEffect(() => {
        // An inline consumer ref can detach the list before newly mounted row
        // layout effects measure it, including on a height-only AutoSizer change.
        if(list.current && pendingReset.current != null) {
            const index = pendingReset.current;
            pendingReset.current = null;
            list.current.resetAfterIndex(index);
        }
    });

    return <VariableSizeList {...props} width={width} itemSize={itemSize} itemData={data} ref={attachList}>
        {children}
    </VariableSizeList>;
}
